import React, { useState } from 'react';
import { Box, Button, Collapse, Flex, Input, Text, useToast } from '@chakra-ui/react';
import { CheckCircle, Info, LockSimple, WarningCircle } from 'phosphor-react';

import {
  IExtratoDivergencia,
  useResolverDivergencia,
} from '../../../../../../hooks/useContaCilExtratos';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';
import { fmtValor } from '../format';

/**
 * Quiz de resolução das janelas de divergência NÃO discriminada pelo banco.
 *
 * O ETL provou (pelos totais impressos do extrato) que as transações estão
 * completas — a diferença é movimentação que o banco não imprime como linha
 * (rendimento de conta remunerada / taxa). Cada janela exige uma decisão do
 * contador antes de liberar a conciliação:
 *  - Ignorar: fica registrada como informação (nenhum lançamento é criado);
 *  - Rotular: cria um lançamento com histórico padrão EDITÁVEL
 *    (rendimento/taxa não discriminado) e data default = fim da janela.
 */

const AMBAR = '#B45309';
const AMBAR_BG = 'rgba(245, 158, 11, 0.05)';
const AMBAR_BORDA = 'rgba(245, 158, 11, 0.35)';

function isoFromBr(br?: string | null): string {
  if (!br) return '';
  const m = br.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}

function historicoDefault(valor: number): string {
  return valor > 0 ? 'Rendimento não discriminado pelo banco' : 'Taxa não discriminada pelo banco';
}

function JanelaItem({
  extratoId,
  indice,
  janela,
  somenteLeitura,
}: {
  extratoId: number;
  indice: number;
  janela: IExtratoDivergencia;
  somenteLeitura?: boolean;
}) {
  const toast = useToast();
  const resolver = useResolverDivergencia();
  const [rotulando, setRotulando] = useState(false);
  const [acaoEmVoo, setAcaoEmVoo] = useState<'ignorar' | 'rotular' | null>(null);
  const [historico, setHistorico] = useState(historicoDefault(janela.valor));
  const [data, setData] = useState(isoFromBr(janela.ate));

  const pendente = janela.status === 'pendente';
  const positivo = janela.valor > 0;

  const submit = (acao: 'ignorar' | 'rotular') => {
    setAcaoEmVoo(acao);
    resolver.mutate(
      {
        extratoId,
        indice,
        acao,
        historico: acao === 'rotular' ? historico : undefined,
        data: acao === 'rotular' && data ? data : undefined,
        // fingerprint anti-índice-stale (reprocesso muda a lista)
        de: janela.de ?? null,
        ate: janela.ate ?? null,
      },
      {
        onSuccess: r => {
          toast({ status: 'success', title: r.mensagem, duration: 4000 });
          setRotulando(false);
          setAcaoEmVoo(null);
        },
        onError: err => {
          setAcaoEmVoo(null);
          toast({
            status: 'error',
            title: 'Não foi possível resolver a janela',
            description: extractApiErrorMessage(err),
            duration: 6000,
          });
        },
      },
    );
  };

  return (
    <Box
      borderWidth="1px"
      borderColor={pendente ? AMBAR_BORDA : 'rgba(15, 23, 42, 0.08)'}
      borderRadius="lg"
      p={3}
      bg={pendente ? 'white' : 'rgba(15, 23, 42, 0.02)'}
    >
      <Flex align="center" justify="space-between" gap={2} wrap="wrap">
        <Flex align="center" gap={2} minW={0}>
          <Text fontSize="12px" fontWeight="700" color="#0F172A">
            {janela.de ?? '—'} → {janela.ate ?? '—'}
          </Text>
          <Text fontSize="13px" fontWeight="800" color={positivo ? '#059669' : '#DC2626'}>
            {positivo ? '+' : ''}
            {fmtValor(janela.valor)}
          </Text>
          {janela.saldo_antes != null && janela.saldo_depois != null && (
            <Text fontSize="11px" color="#94A3B8">
              (saldo {fmtValor(janela.saldo_antes)} → {fmtValor(janela.saldo_depois)})
            </Text>
          )}
          {janela.prova_totais && (
            <Text
              fontSize="10px"
              color="#059669"
              fontWeight="600"
              title="Transações conferidas contra os totais impressos no extrato"
            >
              prova ✓
            </Text>
          )}
        </Flex>

        {pendente && somenteLeitura && (
          <Text fontSize="11px" color="#94A3B8" flexShrink={0}>
            Pendente — libere o extrato para resolver
          </Text>
        )}
        {pendente && !somenteLeitura && (
          <Flex gap={2} flexShrink={0}>
            <Button
              size="xs"
              variant="outline"
              borderRadius="lg"
              isLoading={resolver.isPending && acaoEmVoo === 'ignorar'}
              isDisabled={resolver.isPending && acaoEmVoo !== 'ignorar'}
              onClick={() => submit('ignorar')}
            >
              Ignorar (manter como informação)
            </Button>
            <Button
              size="xs"
              borderRadius="lg"
              bg="#F59E0B"
              color="white"
              _hover={{ bg: '#D97706' }}
              isDisabled={resolver.isPending}
              onClick={() => setRotulando(v => !v)}
            >
              Rotular como lançamento
            </Button>
          </Flex>
        )}
        {!pendente && (
          <Flex align="center" gap={1.5} flexShrink={0}>
            <CheckCircle size={14} weight="fill" color="#059669" />
            <Text fontSize="11px" color="#64748B">
              {janela.status === 'ignorado' ? 'Ignorada' : 'Rotulada como lançamento'}
              {janela.resolvido_por ? ` · ${janela.resolvido_por}` : ''}
            </Text>
          </Flex>
        )}
      </Flex>

      {/* Mini-form do rotular: histórico padrão EDITÁVEL + data */}
      <Collapse in={pendente && rotulando} animateOpacity>
        <Flex mt={3} gap={2} wrap="wrap" align="flex-end">
          <Box flex="1" minW="220px">
            <Text fontSize="10px" textTransform="uppercase" color="#94A3B8" fontWeight="700" mb={1}>
              Histórico do lançamento
            </Text>
            <Input
              size="sm"
              borderRadius="lg"
              value={historico}
              maxLength={255}
              onChange={ev => setHistorico(ev.target.value)}
            />
          </Box>
          <Box w="150px">
            <Text fontSize="10px" textTransform="uppercase" color="#94A3B8" fontWeight="700" mb={1}>
              Data
            </Text>
            <Input
              size="sm"
              borderRadius="lg"
              type="date"
              value={data}
              onChange={ev => setData(ev.target.value)}
            />
          </Box>
          <Button
            size="sm"
            borderRadius="lg"
            bg="#F59E0B"
            color="white"
            _hover={{ bg: '#D97706' }}
            isLoading={resolver.isPending && acaoEmVoo === 'rotular'}
            isDisabled={!historico.trim() || (resolver.isPending && acaoEmVoo !== 'rotular')}
            onClick={() => submit('rotular')}
          >
            Criar lançamento
          </Button>
        </Flex>
        <Text fontSize="11px" color="#94A3B8" mt={1}>
          O lançamento nasce pendente de conciliação ({positivo ? 'crédito' : 'débito'} de{' '}
          {fmtValor(Math.abs(janela.valor))}).
        </Text>
      </Collapse>
    </Box>
  );
}

export function DivergenciaQuiz({
  extratoId,
  janelas,
  somenteLeitura = false,
}: {
  extratoId: number;
  janelas?: IExtratoDivergencia[] | null;
  // Extrato ainda não liberado (retido pelo auditor / processando): mostra as
  // janelas como informação mas NÃO permite resolver — rotular criaria
  // lançamento avulso num extrato cujos lançamentos nem foram materializados.
  somenteLeitura?: boolean;
}) {
  if (!janelas || janelas.length === 0) return null;
  const pendentes = janelas.filter(j => j.status === 'pendente').length;
  const todasProvadas = janelas.every(j => j.prova_totais);

  return (
    <Box borderWidth="1px" borderColor={AMBAR_BORDA} bg={AMBAR_BG} borderRadius="xl" p={4} mb={4}>
      <Flex align="center" gap={2} mb={1}>
        <Box color={AMBAR}>
          <WarningCircle size={16} weight="fill" />
        </Box>
        <Text fontSize="13px" fontWeight="700" color={AMBAR}>
          Divergência não discriminada pelo banco
        </Text>
      </Flex>
      <Text fontSize="12px" color="#64748B" mb={3}>
        O saldo varia sem lançamento impresso pelo banco — comum em conta remunerada (rendimento) ou
        taxa não discriminada.
        {todasProvadas
          ? ' As transações do extrato foram conferidas contra os totais impressos: nenhuma linha foi perdida.'
          : ''}
        {somenteLeitura
          ? ' Resolva as janelas após o extrato estar concluído/liberado.'
          : ' Decida o destino de cada janela abaixo.'}
      </Text>

      <Flex direction="column" gap={2}>
        {janelas.map((j, i) => (
          <JanelaItem
            // key estável por CONTEÚDO (datas+valor): se um reprocesso mudar
            // as janelas com o modal aberto, o item REMONTA e o estado
            // interno (histórico/data do form) não vaza pra janela errada.
            // O índice compõe a key só p/ desempate de janelas idênticas.
            // eslint-disable-next-line react/no-array-index-key
            key={`${j.de ?? '?'}|${j.ate ?? '?'}|${j.valor}|${i}`}
            extratoId={extratoId}
            indice={i}
            janela={j}
            somenteLeitura={somenteLeitura}
          />
        ))}
      </Flex>

      {pendentes > 0 ? (
        <Flex align="center" gap={1.5} mt={3}>
          <LockSimple size={13} weight="fill" color={AMBAR} />
          <Text fontSize="11px" color={AMBAR} fontWeight="600">
            Conciliação bloqueada até resolver {pendentes} janela{pendentes > 1 ? 's' : ''} pendente
            {pendentes > 1 ? 's' : ''}.
          </Text>
        </Flex>
      ) : (
        <Flex align="center" gap={1.5} mt={3}>
          <Info size={13} weight="fill" color="#059669" />
          <Text fontSize="11px" color="#059669" fontWeight="600">
            Todas as janelas resolvidas — conciliação liberada. O aviso permanece como informação.
          </Text>
        </Flex>
      )}
    </Box>
  );
}
